import React, { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';
import { weather } from './weather';
import { sky } from './skyState';
import { streetU } from './streetMaterials';
import { IS_MOBILE } from '../config';
import { rainSet } from '../game/sfx';

// ── RAIN ─────────────────────────────────────────────────────────────────────
//
// Thousands of streaks in a box that travels with the camera, animated
// entirely on the GPU: every drop is a quad whose position is a function of
// time and its own seed, wrapped around the camera so the box never has an
// edge you can walk out of. One draw call.
//
// The streaks take their light from the street: each one samples the lamp-pool
// texture under it, so rain falling through a streetlight glows in the lamp's
// colour and rain in the dark between lamps barely shows — which is exactly how
// rain looks in a city at night.

const COUNT = IS_MOBILE ? 2600 : 9000;
const BOX = new THREE.Vector3(34, 16, 34);

const VERT = /* glsl */`
attribute vec4 seed;            // x, z in [0,1), phase in [0,1), length scale
uniform float uTime;
uniform vec3 uCam;
uniform vec3 uBox;
uniform vec2 uWind;
uniform float uSpeed;
varying float vT;
varying vec3 vWorld;
varying float vFade;
void main() {
  // Wrap the drop's column around the camera.
  vec2 col = seed.xy * uBox.xz;
  vec2 base = uCam.xz - uBox.xz * 0.5;
  vec2 xz = base + mod(col - base, uBox.xz);
  float fall = mod(seed.z * uBox.y - uTime * uSpeed, uBox.y);
  float y = uCam.y - 3.0 + fall;
  float len = 0.28 * seed.w;
  // position.y in [0,1] picks the head or the tail; position.x the side.
  vec3 dir = normalize(vec3(uWind.x, -1.0, uWind.y));
  vec3 head = vec3(xz.x, y, xz.y);
  vec3 p = head - dir * len * position.y;
  vec4 mv = viewMatrix * vec4(p, 1.0);
  // Widen sideways in view space so the streak always faces the camera.
  vec3 vdir = normalize((viewMatrix * vec4(dir, 0.0)).xyz);
  vec3 side = normalize(cross(vdir, vec3(0.0, 0.0, 1.0)));
  mv.xyz += side * position.x * 0.0085;
  vT = position.y;
  vWorld = p;
  float d = length(p - uCam);
  vFade = smoothstep(0.6, 1.6, d) * (1.0 - smoothstep(12.0, 17.0, d));
  gl_Position = projectionMatrix * mv;
}
`;

const FRAG = /* glsl */`
uniform float uI;
uniform vec3 uAmb;
uniform sampler2D uPoolTex;
uniform vec4 uPoolRect;
uniform float uPoolI;
varying float vT;
varying vec3 vWorld;
varying float vFade;
void main() {
  vec2 puv = (vWorld.xz - uPoolRect.xy) / uPoolRect.zw;
  vec3 lamp = vec3(0.0);
  if (puv.x > 0.0 && puv.x < 1.0 && puv.y > 0.0 && puv.y < 1.0) {
    lamp = texture2D(uPoolTex, puv).rgb * 4.0 * uPoolI * exp(-max(vWorld.y - 1.0, 0.0) / 4.0);
  }
  float a = (1.0 - vT) * vFade * uI;
  gl_FragColor = vec4((uAmb + lamp * 0.5) * a, a);
}
`;

export default function Rain() {
  const geo = useMemo(() => {
    // One quad per drop: (side −1/1) × (head 0 / tail 1).
    const g = new THREE.InstancedBufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute([-1, 0, 0, 1, 0, 0, -1, 1, 0, 1, 1, 0], 3));
    g.setIndex([0, 2, 1, 1, 2, 3]);
    const seeds = new Float32Array(COUNT * 4);
    for (let i = 0; i < COUNT; i++) {
      seeds[i * 4] = Math.random(); seeds[i * 4 + 1] = Math.random();
      seeds[i * 4 + 2] = Math.random(); seeds[i * 4 + 3] = 0.6 + Math.random() * 0.8;
    }
    g.setAttribute('seed', new THREE.InstancedBufferAttribute(seeds, 4));
    g.instanceCount = COUNT;
    return g;
  }, []);
  const mat = useMemo(() => new THREE.ShaderMaterial({
    uniforms: {
      uTime: { value: 0 }, uCam: { value: new THREE.Vector3() }, uBox: { value: BOX },
      uWind: { value: new THREE.Vector2(0.12, 0.05) }, uSpeed: { value: 15 }, uI: { value: 0 },
      uAmb: { value: new THREE.Color() },
      uPoolTex: streetU.uPoolTex, uPoolRect: streetU.uPoolRect, uPoolI: streetU.uPoolI,
    },
    vertexShader: VERT,
    fragmentShader: FRAG,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    fog: false,
  }), []);
  useEffect(() => () => { geo.dispose(); mat.dispose(); rainSet(0); }, [geo, mat]);
  const mesh = useRef();

  useFrame(({ camera, clock }) => {
    const r = weather.rain;
    rainSet(r);
    if (mesh.current) mesh.current.visible = r > 0.01;
    if (r <= 0.01) return;
    const u = mat.uniforms;
    u.uTime.value = clock.elapsedTime;
    u.uCam.value.copy(camera.position);
    // Fewer drops in light rain: the alpha does it (all are drawn either way;
    // the fill cost is what alpha saves on, and it is small).
    u.uI.value = Math.min(1, r * 1.2) * (weather.storm ? 1.25 : 1);
    // Ambient light the drops catch: the sky by day, a little at night.
    u.uAmb.value.copy(sky.fogColor).multiplyScalar(0.55 + 0.6 * (1 - sky.night)).addScalar(weather.flash * 0.8);
    u.uWind.value.set(0.12 + (weather.storm ? 0.18 : 0), 0.05);
  });

  return <mesh ref={mesh} geometry={geo} material={mat} frustumCulled={false} renderOrder={6} userData={{ noShadow: true }} />;
}
